import { Environment, ContactShadows, Text, Html } from '@react-three/drei';
import * as THREE from 'three';
import { useThemeStore } from '../../titration/store/themeStore';
import AnalogClock from '../../titration/lab/AnalogClock';
import PeriodicTablePoster from '../../titration/lab/PeriodicTablePoster';
import SideDesk from '../../titration/lab/SideDesk';
import EmergencyShower from '../../titration/lab/EmergencyShower';

// Checkered vinyl floor tiles — classic chemistry lab look
function CheckeredFloor() {
    const tileSize = 1.0;
    const gridCount = 15; // 15x15 tiles
    const tiles = [];
    for (let x = -Math.floor(gridCount / 2); x <= Math.floor(gridCount / 2); x++) {
        for (let z = -Math.floor(gridCount / 2); z <= Math.floor(gridCount / 2); z++) {
            const isLight = (x + z) % 2 === 0;
            tiles.push(
                <mesh
                    key={`${x}-${z}`}
                    rotation={[-Math.PI / 2, 0, 0]}
                    position={[x * tileSize, -3.001, z * tileSize]}
                    receiveShadow
                >
                    <planeGeometry args={[tileSize, tileSize]} />
                    <meshStandardMaterial
                        color={isLight ? '#b8b8b2' : '#8a8a84'}
                        roughness={0.75}
                        metalness={0.02}
                    />
                </mesh>
            );
        }
    }
    return <>{tiles}</>;
}

// Fluorescent ceiling fixture
// Fluorescent ceiling fixture
function FluorescentFixture({ position, isOn = true }: { position: [number, number, number], isOn?: boolean }) {
    return (
        <group position={position}>
            {/* Housing */}
            <mesh>
                <boxGeometry args={[1.4, 0.06, 0.22]} />
                <meshStandardMaterial color="#c8c8c4" roughness={0.7} metalness={0.3} />
            </mesh>
            {/* Diffuser Panel — emissive glow */}
            <mesh position={[0, -0.04, 0]}>
                <boxGeometry args={[1.3, 0.01, 0.18]} />
                <meshStandardMaterial
                    color={isOn ? "#ffffff" : "#666666"}
                    emissive={isOn ? "#d8eeff" : "#000000"}
                    emissiveIntensity={isOn ? 2.5 : 0}
                    roughness={0.5}
                />
            </mesh>
        </group>
    );
}

// Wall Light Switch
function LightSwitch({ position, isOn, onToggle, label }: { position: [number, number, number], isOn: boolean, onToggle: () => void, label: string }) {
    return (
        <group position={position}>
            {/* Label */}
            <Text position={[0, 0.16, 0.02]} fontSize={0.05} color="#333333" anchorX="center" anchorY="bottom" letterSpacing={-0.05}>
                {label}
            </Text>
            {/* Clickable Area */}
            <group onClick={(e) => { e.stopPropagation(); onToggle(); }} onPointerOver={() => document.body.style.cursor = 'pointer'} onPointerOut={() => document.body.style.cursor = 'auto'}>
                {/* Wall Plate */}
                <mesh position={[0, 0, 0.01]} castShadow receiveShadow>
                    <boxGeometry args={[0.12, 0.2, 0.02]} />
                    <meshStandardMaterial color="#e0e0e0" roughness={0.8} />
                </mesh>
                {/* Switch Toggle */}
                <mesh position={[0, isOn ? 0.03 : -0.03, 0.025]} rotation={[isOn ? -0.2 : 0.2, 0, 0]} castShadow>
                    <boxGeometry args={[0.04, 0.08, 0.02]} />
                    <meshStandardMaterial color="#f0f0f0" roughness={0.7} />
                </mesh>
            </group>
        </group>
    );
}

export default function LabRoom() {
    const { resolvedTheme, setTheme } = useThemeStore();
    const isDarkMode = resolvedTheme === 'dark';

    // Sync lights with dark mode toggle instead of keeping them isolated
    const isLight1On = !isDarkMode;
    const isLight2On = !isDarkMode;

    const toggleDarkLight = () => {
        setTheme(isDarkMode ? 'light' : 'dark');
    };

    const lightRatio = (isLight1On ? 0.5 : 0) + (isLight2On ? 0.5 : 0);

    return (
        <>
            {/* === LIGHTING — Cool white fluorescent, 5500K === */}

            {/* Soft ambient fill — slightly warm so shadows aren't pure black */}
            <ambientLight intensity={isLight1On && isLight2On ? 0.35 : (isLight1On || isLight2On ? 0.2 : 0.05)} color="#cce4ff" />

            {/* Main overhead key light — slightly forward to avoid flat look */}
            <directionalLight
                position={[0, 6, 2]}
                intensity={1.4 * lightRatio + 0.1}
                castShadow
                shadow-mapSize-width={2048}
                shadow-mapSize-height={2048}
                shadow-camera-near={0.5}
                shadow-camera-far={25}
                shadow-camera-left={-4}
                shadow-camera-right={4}
                shadow-camera-top={4}
                shadow-camera-bottom={-4}
                color="#e8f0ff"
                shadow-bias={-0.001}
            />

            {/* Left fill — counteracts harsh shadows */}
            <pointLight position={[-3, 4, 1]} intensity={isLight1On ? 0.7 : 0.05} color="#ddeeff" distance={10} />
            {/* Right fill */}
            <pointLight position={[3, 4, 1]} intensity={isLight2On ? 0.7 : 0.05} color="#ddeeff" distance={10} />
            {/* Front bounce — simulates light bouncing off bench surface */}
            <pointLight position={[0, 0.5, 4]} intensity={0.3 * lightRatio + 0.02} color="#fffbe8" distance={6} />

            {/* Glass-quality environment map */}
            <Environment preset="warehouse" environmentIntensity={0.25} />

            {/* Room environment provides base shadows, removing isolated ContactShadows to prevent glitching on the desk */}

            {/* === ROOM === */}

            {/* Floor — checkered vinyl tiles */}
            <CheckeredFloor />

            {/* Back wall — light gray (#d6d6d0) */}
            <mesh position={[0, 2.5, -5]} receiveShadow>
                <planeGeometry args={[20, 12]} />
                <meshStandardMaterial color="#d0d0ca" roughness={0.95} />
            </mesh>

            {/* Left side wall */}
            <mesh position={[-7, 2.5, 0]} rotation={[0, Math.PI / 2, 0]} receiveShadow>
                <planeGeometry args={[14, 12]} />
                <meshStandardMaterial color="#cacac4" roughness={0.95} side={THREE.DoubleSide} />
            </mesh>

            {/* Right side wall */}
            <mesh position={[7, 2.5, 0]} rotation={[0, -Math.PI / 2, 0]} receiveShadow>
                <planeGeometry args={[14, 12]} />
                <meshStandardMaterial color="#cacac4" roughness={0.95} side={THREE.DoubleSide} />
            </mesh>


            {/* === BASEBOARD TRIM === */}
            {/* Back wall baseboard */}
            <mesh position={[0, -2.9, -4.97]}>
                <boxGeometry args={[20, 0.15, 0.04]} />
                <meshStandardMaterial color="#a0a098" roughness={0.7} />
            </mesh>
            {/* Left wall baseboard */}
            <mesh position={[-6.97, -2.9, 0]} rotation={[0, Math.PI / 2, 0]}>
                <boxGeometry args={[14, 0.15, 0.04]} />
                <meshStandardMaterial color="#a0a098" roughness={0.7} />
            </mesh>
            {/* Right wall baseboard */}
            <mesh position={[6.97, -2.9, 0]} rotation={[0, -Math.PI / 2, 0]}>
                <boxGeometry args={[14, 0.15, 0.04]} />
                <meshStandardMaterial color="#a0a098" roughness={0.7} />
            </mesh>

            {/* === WALL DECORATIONS === */}

            {/* Safety poster — left wall */}
            <group position={[-6.94, 1.5, -1.5]} rotation={[0, Math.PI / 2, 0]}>
                {/* Thin border — behind */}
                <mesh position={[0, 0, -0.015]}>
                    <planeGeometry args={[1.06, 1.46]} />
                    <meshStandardMaterial color="#333333" roughness={0.9} />
                </mesh>
                {/* White poster board — in front */}
                <mesh position={[0, 0, 0]}>
                    <planeGeometry args={[1.0, 1.4]} />
                    <meshStandardMaterial color="#ffffff" roughness={0.92} />
                </mesh>

                {/* Red header bar */}
                <mesh position={[0, 0.56, 0.01]}>
                    <planeGeometry args={[1.0, 0.26]} />
                    <meshStandardMaterial color="#cc0000" roughness={0.85} />
                </mesh>
                <Text position={[0, 0.56, 0.02]} fontSize={0.09} color="#ffffff"
                    anchorX="center" anchorY="middle" fontWeight={700}>
                    SAFETY FIRST
                </Text>

                {/* Section 1 — Goggles */}
                <mesh position={[-0.35, 0.30, 0.01]}>
                    <circleGeometry args={[0.06, 20]} />
                    <meshStandardMaterial color="#2563eb" />
                </mesh>
                <Text position={[-0.35, 0.30, 0.02]} fontSize={0.05} color="#ffffff"
                    anchorX="center" anchorY="middle" fontWeight={700}>G</Text>
                <Text position={[0.05, 0.32, 0.015]} fontSize={0.045} color="#1a1a1a"
                    anchorX="left" anchorY="middle" fontWeight={700}>
                    Wear safety goggles
                </Text>
                <Text position={[0.05, 0.26, 0.015]} fontSize={0.035} color="#555555"
                    anchorX="left" anchorY="middle">
                    at all times
                </Text>

                {/* Divider line */}
                <mesh position={[0, 0.19, 0.01]}>
                    <planeGeometry args={[0.85, 0.005]} />
                    <meshStandardMaterial color="#dddddd" />
                </mesh>

                {/* Section 2 — No food */}
                <mesh position={[-0.35, 0.08, 0.01]}>
                    <circleGeometry args={[0.06, 20]} />
                    <meshStandardMaterial color="#dc2626" />
                </mesh>
                <Text position={[-0.35, 0.08, 0.02]} fontSize={0.05} color="#ffffff"
                    anchorX="center" anchorY="middle" fontWeight={700}>X</Text>
                <Text position={[0.05, 0.10, 0.015]} fontSize={0.045} color="#1a1a1a"
                    anchorX="left" anchorY="middle" fontWeight={700}>
                    No food or drinks
                </Text>
                <Text position={[0.05, 0.04, 0.015]} fontSize={0.035} color="#555555"
                    anchorX="left" anchorY="middle">
                    in the laboratory
                </Text>

                {/* Divider line */}
                <mesh position={[0, -0.03, 0.01]}>
                    <planeGeometry args={[0.85, 0.005]} />
                    <meshStandardMaterial color="#dddddd" />
                </mesh>

                {/* Section 3 — Spills */}
                <mesh position={[-0.35, -0.14, 0.01]}>
                    <circleGeometry args={[0.06, 20]} />
                    <meshStandardMaterial color="#f59e0b" />
                </mesh>
                <Text position={[-0.35, -0.14, 0.02]} fontSize={0.05} color="#ffffff"
                    anchorX="center" anchorY="middle" fontWeight={700}>!</Text>
                <Text position={[0.05, -0.12, 0.015]} fontSize={0.045} color="#1a1a1a"
                    anchorX="left" anchorY="middle" fontWeight={700}>
                    Report all spills
                </Text>
                <Text position={[0.05, -0.18, 0.015]} fontSize={0.035} color="#555555"
                    anchorX="left" anchorY="middle">
                    immediately
                </Text>

                {/* Bottom danger bar */}
                <mesh position={[0, -0.58, 0.01]}>
                    <planeGeometry args={[1.0, 0.12]} />
                    <meshStandardMaterial color="#cc0000" roughness={0.85} />
                </mesh>
                <Text position={[0, -0.58, 0.02]} fontSize={0.045} color="#ffffff"
                    anchorX="center" anchorY="middle" fontWeight={700}>
                    DANGER: AUTHORIZED PERSONNEL ONLY
                </Text>
            </group>

            {/* Periodic Table poster — back wall (realistic with real elements) */}
            {/* Periodic Table poster — back wall (realistic with real elements) */}
            <PeriodicTablePoster position={[-4.0, 3.5, -4.96]} />

            {/* === EMERGENCY SHOWER & EYEWASH — left side of lab === */}
            <EmergencyShower position={[-6.5, -0.62, -3.5]} />

            {/* === ANALOG CLOCK — back wall, upper right === */}
            <AnalogClock position={[5.5, 3.2, -4.93]} />

            {/* === SIDE DESK WITH LAB APPARATUS — right wall === */}
            {/* Countertop at Y=-0.62 (same as main bench), cabinet drops to floor (Y=-3) */}
            <SideDesk position={[5.8, -0.62, -1.0]} mirror />

            {/* Ceiling — off-white */}
            <mesh rotation={[Math.PI / 2, 0, 0]} position={[0, 5.5, 0]}>
                <planeGeometry args={[20, 14]} />
                <meshStandardMaterial color="#eaeae6" roughness={1.0} />
            </mesh>

            {/* === CEILING FLUORESCENT FIXTURES === */}
            <FluorescentFixture position={[-2.5, 5.44, 0]} isOn={isLight1On} />
            {isLight1On && <pointLight position={[-2.5, 5, 0]} intensity={1.5} color="#d8eeff" distance={8} />}

            <FluorescentFixture position={[2.5, 5.44, 0]} isOn={isLight2On} />
            {isLight2On && <pointLight position={[2.5, 5, 0]} intensity={1.5} color="#d8eeff" distance={8} />}

            {/* === WALL LIGHT SWITCHES === */}
            <group position={[5.5, 1.2, -4.93]}>
                <LightSwitch position={[-0.1, 0, 0]} isOn={isLight1On} onToggle={toggleDarkLight} label="L1" />
                <LightSwitch position={[0.1, 0, 0]} isOn={isLight2On} onToggle={toggleDarkLight} label="L2" />
            </group>

            {/* === LAB BENCH — Natural oak wood with chemical-resistant clearcoat === */}
            {/* Main bench top — medium oak wood */}
            <mesh position={[0, -0.62, -0.5]} receiveShadow castShadow>
                <boxGeometry args={[5.5, 0.07, 2.4]} />
                <meshPhysicalMaterial
                    color="#8b5e3c"
                    roughness={0.55}
                    metalness={0.0}
                    clearcoat={0.45}
                    clearcoatRoughness={0.35}
                />
            </mesh>

            {/* Wood grain lines (light streaks) — kept within the bench top z range [-1.7, 0.7] */}
            {[-1.0, -0.5, 0.0, 0.5, 1.0].map((z, i) => (
                <mesh key={i} position={[0, -0.584, z - 0.5]}>
                    <boxGeometry args={[5.4, 0.001, 0.008]} />
                    <meshStandardMaterial color="#6b4423" roughness={1} />
                </mesh>
            ))}

            {/* Bench front edge strip */}
            <mesh position={[0, -0.66, 0.7]} receiveShadow>
                <boxGeometry args={[5.5, 0.01, 0.08]} />
                <meshStandardMaterial color="#6b4423" roughness={0.6} />
            </mesh>

            {/* Front fascia panel */}
            <mesh position={[0, -1.1, 0.72]} receiveShadow>
                <boxGeometry args={[5.5, 0.9, 0.04]} />
                <meshStandardMaterial color="#d0cec8" roughness={0.85} />
            </mesh>

            {/* Bench base/legs */}
            {[-2.3, 2.3].map((x, i) => (
                <mesh key={i} position={[x, -1.6, -0.5]} receiveShadow>
                    <boxGeometry args={[0.07, 1.9, 2.35]} />
                    <meshStandardMaterial color="#b8b6b0" roughness={0.7} />
                </mesh>
            ))}

            {/* Under-bench cabinet back panel */}
            <mesh position={[0, -1.6, -1.72]} receiveShadow>
                <boxGeometry args={[5.5, 1.9, 0.04]} />
                <meshStandardMaterial color="#c4c2bc" roughness={0.9} />
            </mesh>
        </>
    );
}
